import React, { useEffect, useState } from "react";
import MappedB2CProducts from "./mappedB2CProducts";
import UnmappedB2CProducts from "./unmappedB2CProductsSimple";
import NoMatchFoundB2CProducts from "./noMatchFoundB2CProducts";
import ProductMappingStatistics from "./ProductMappingStatistics";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { AlertCircle, CheckCircle, Ban } from "lucide-react";

const B2CProductsMapping = () => {
	const [entities, setEntities] = useState<any>([]);
	const [activeTab, setActiveTab] = useState("unmapped");
	const [selectedEntity, setSelectedEntity] = useState<any>(null);
	const [mappedPage, setMappedPage] = useState(1);
	const [unmappedPage, setUnmappedPage] = useState(1);
	const [noMatchFoundPage, setNoMatchFoundPage] = useState(1);
	const [showOnlyOtc, setShowOnlyOtc] = useState(false);
	const [showMrpZero, setShowMrpZero] = useState(false);
	const [mappedCount, setMappedCount] = useState(0);
	const [unmappedCount, setUnmappedCount] = useState(0);
	const [noMatchFoundCount, setNoMatchFoundCount] = useState(0);
	const [statistics, setStatistics] = useState<any>(null);
	const [entitiesLoaded, setEntitiesLoaded] = useState(false);
	const { userEntities } = useAuth();

	// Load entities on mount
	useEffect(() => {
		api.getB2CEntities().then(data => {
			if (data && data.results && data.results.length > 0) {
				let formattedEntities = data.results.map((entity: any) => ({
					value: entity.id || entity.branch_id,
					label: `${entity.name} (Branch: ${entity.branch_id})`,
					branch_id: entity.branch_id,
					name: entity.name
				}));

				if (userEntities && userEntities.length > 0) {
					const allowedBranchIds = new Set(
						userEntities
							.map(e => e.branch_id)
							.filter((b): b is number => b !== null && b !== undefined),
					);
					formattedEntities = formattedEntities.filter(
						(entity: any) => allowedBranchIds.has(entity.branch_id),
					);
				}

				const filteredEntities = formattedEntities.filter((entity: any) => entity.value !== 0);
				setEntities(filteredEntities);
				setEntitiesLoaded(true);
			} else {
				setEntitiesLoaded(true);
			}
		}).catch(() => {
			setEntitiesLoaded(true);
		});
	}, [userEntities]);

	// Function to refresh statistics
	const refreshStatistics = async () => {
		const entityId = selectedEntity ? selectedEntity.value : null;

		try {
			const response = await api.getProductMappingStatistics(entityId, showOnlyOtc, showMrpZero);
			if (response.success) {
				setStatistics(response.data);
				setMappedCount(response.data.mappedProducts || 0);
				setUnmappedCount(response.data.unmappedProducts || 0);
				setNoMatchFoundCount(response.data.noMatchFoundProducts || 0);
			}
		} catch (error) {
			// Error fetching statistics
		}
	};

	// Fetch statistics when entity or OTC mode changes
	// Only fetch if entities are loaded to prevent multiple calls
	useEffect(() => {
		if (!entitiesLoaded) return;
		refreshStatistics();
	}, [selectedEntity, showOnlyOtc, showMrpZero, entitiesLoaded]);

	const handleEntityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const selectedValue = e.target.value;

		if (selectedValue === "") {
			setSelectedEntity(null);
		} else {
			const selected = entities.find((entity: any) => entity.value === Number(selectedValue));
			setSelectedEntity(selected || null);
		}
		// Reset page when entity changes
		setMappedPage(1);
		setUnmappedPage(1);
		setNoMatchFoundPage(1);
	};

	const handleOtcToggle = (checked: boolean) => {
		setShowOnlyOtc(checked);
	};

	return (
		<div className="min-h-screen bg-gray-100 dark:bg-gray-900">
			{/* OTC Toggle */}
			<div className="px-6 pt-4 pb-2">
				<div className="flex justify-end">
					<div className={`flex items-center gap-3 px-4 py-2.5 rounded-full transition-all duration-300 ${
						showOnlyOtc
							? 'bg-gray-700 border border-gray-500 shadow-lg'
							: 'bg-gray-800/70 border border-gray-600 hover:bg-gray-800 hover:border-gray-500'
					}`}>
						<Label
							htmlFor="otc-mode-top"
							className={`text-sm font-semibold cursor-pointer select-none transition-colors ${
								showOnlyOtc
									? 'text-white'
									: 'text-gray-400 hover:text-gray-300'
							}`}
						>
							OTC Only
						</Label>
						<Switch
							id="otc-mode-top"
							checked={showOnlyOtc}
							onCheckedChange={handleOtcToggle}
							className={`${
								showOnlyOtc
									? 'data-[state=checked]:bg-white'
									: 'data-[state=unchecked]:bg-gray-600'
							}`}
						/>
					</div>
				</div>
			</div>

			{/* Statistics Cards */}
			<div className="px-6 pb-2">
				<ProductMappingStatistics
					entityId={selectedEntity?.value || null}
					showOnlyOtc={showOnlyOtc}
					showMrpZero={showMrpZero}
					statistics={statistics}
					loading={false}
				/>
			</div>

			{/* Main Content Area */}
			<div className="px-6 pb-6 mt-4">
				{/* Pharmacy Selection Card */}
				<div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-6 mb-6">
					<h2 className="text-xl font-bold text-white mb-2">Pharmacy Selection</h2>
					<p className="text-gray-400 text-sm mb-4">
						Select a pharmacy to view its products. Leave empty to view all products.
					</p>
					<select
						className="w-full max-w-lg px-4 py-3 bg-gray-700 dark:bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
						value={selectedEntity?.value || ""}
						onChange={handleEntityChange}
					>
						<option value="">All Pharmacies</option>
						{entities.map((entity: any) => (
							<option key={entity.value} value={entity.value}>
								{entity.label}
							</option>
						))}
					</select>
				</div>

				{/* Products Table Card - Show even when no entity selected */}
				<div className="bg-gray-800 dark:bg-gray-900 rounded-lg">
					{/* Header with title and tab controls */}
					<div className="px-6 py-4 border-b border-gray-700">
						<div className="flex justify-between items-center">
							<h2 className="text-xl font-bold text-white">
								Products - {selectedEntity ? (selectedEntity.name || selectedEntity.label.split('(')[0].trim()) : 'All Pharmacies'}
							</h2>
							<div className="flex items-center gap-4">
								{/* Tab Toggle */}
								<div className="flex bg-gray-700 rounded-lg p-1">
									<Button
										variant={activeTab === 'unmapped' ? 'default' : 'ghost'}
										size="sm"
										onClick={() => setActiveTab('unmapped')}
										className={`px-4 py-2 rounded-md transition-colors ${
											activeTab === 'unmapped'
												? 'bg-orange-600 text-white hover:bg-orange-700'
												: 'text-gray-300 hover:text-white hover:bg-gray-600'
										}`}
									>
										<AlertCircle className="w-4 h-4 mr-2" />
										Unmapped ({unmappedCount})
									</Button>
									<Button
										variant={activeTab === 'mapped' ? 'default' : 'ghost'}
										size="sm"
										onClick={() => setActiveTab('mapped')}
										className={`px-4 py-2 rounded-md transition-colors ${
											activeTab === 'mapped'
												? 'bg-green-600 text-white hover:bg-green-700'
												: 'text-gray-300 hover:text-white hover:bg-gray-600'
										}`}
									>
										<CheckCircle className="w-4 h-4 mr-2" />
										Mapped ({mappedCount})
									</Button>
									<Button
										variant={activeTab === 'no_match_found' ? 'default' : 'ghost'}
										size="sm"
										onClick={() => setActiveTab('no_match_found')}
										className={`px-4 py-2 rounded-md transition-colors ${
											activeTab === 'no_match_found'
												? 'bg-red-600 text-white hover:bg-red-700'
												: 'text-gray-300 hover:text-white hover:bg-gray-600'
										}`}
									>
										<Ban className="w-4 h-4 mr-2" />
										No Match Found ({noMatchFoundCount})
									</Button>
								</div>

								{/* MRP Filter */}
								<Button
									variant={showMrpZero ? 'default' : 'outline'}
									size="sm"
									onClick={() => setShowMrpZero(!showMrpZero)}
									className={`px-4 py-2 ${
										showMrpZero
											? 'bg-blue-600 text-white hover:bg-blue-700 border-0'
											: 'border-gray-600 text-gray-300 hover:bg-gray-700'
									}`}
								>
									{showMrpZero ? 'Hide' : 'Show'} MRP ₹0
								</Button>
							</div>
						</div>
					</div>

					{/* Table Content */}
					<div className="">
						{activeTab === 'unmapped' ? (
							<UnmappedB2CProducts
								entities={entities}
								selectedEntity={selectedEntity}
								onEntityChange={setSelectedEntity}
								page={unmappedPage}
								onPageChange={setUnmappedPage}
								showOnlyOtc={showOnlyOtc}
								showMrpZero={showMrpZero}
								onStatisticsUpdate={refreshStatistics}
							/>
						) : activeTab === 'mapped' ? (
							<MappedB2CProducts
								entities={entities}
								selectedEntity={selectedEntity}
								onEntityChange={setSelectedEntity}
								page={mappedPage}
								onPageChange={setMappedPage}
								showOnlyOtc={showOnlyOtc}
								showMrpZero={showMrpZero}
								onStatisticsUpdate={refreshStatistics}
							/>
						) : (
							<NoMatchFoundB2CProducts
								entities={entities}
								selectedEntity={selectedEntity}
								onEntityChange={setSelectedEntity}
								page={noMatchFoundPage}
								onPageChange={setNoMatchFoundPage}
								showOnlyOtc={showOnlyOtc}
								showMrpZero={showMrpZero}
								onStatisticsUpdate={refreshStatistics}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default B2CProductsMapping;